import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import { addImportBill, addImportReturnBill, getImportBillById } from "../../../services/Import";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Badge,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TableItem from "../../../components/TableItem";
import CloseIcon from "@mui/icons-material/Close";

// Icons
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StoreIcon from "@mui/icons-material/Store";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import DiscountIcon from "@mui/icons-material/Discount";
import TagIcon from "@mui/icons-material/Tag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import { getUser } from "../../../Utils/Common";

function Im_returnBillConfirmation({ billData, setOpenConfirmation }) {
  const theme = useTheme();
  const history = useHistory();
  const [importBill, setImportBill] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = billData;
      if (result) {
        setImportBill(result);
        const billItemSet = result.import_billItems || [];
        console.log("Bill Item set: ", billItemSet);
        const itemRows = billItemSet.map((item) => ({
          item: item.imports.itemName + " (" + item.imports.product_code + ")",
          brand: item.imports.brand,
          qty: item.bill_qty,
          billUnit: item.billUnit,
        }));

        setRows(itemRows);
      } else {
        setErrors({
          ...errors,
          _billDataError: "Unable to fetch bill data",
        });
      }
    } catch (error) {
      console.error("Error fetching bill data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const submitBill = async () => {
    setLoading(true);
    const selectedRows = billData.import_billItems;
    let billItemList = selectedRows.map((row) => ({
          importId: row.imports.importId,
          returnQty: row.bill_qty,
          billUnit: row.billUnit,
        }));
    

    const billBody = {
      billNo: importBill.billNo,
      addedBy: importBill.addedBy,
      importBillId: importBill.importBillId,
      note: importBill.note,
      import_billItems: billItemList,
    };

    let result = await addImportReturnBill(billBody);
    if (result !== "Something went wrong...") {
      let createdBillData = result;
      console.log("Created Bill Data: ", createdBillData);
      setOpenConfirmation(false);
      setLoading(false);
      history.push("/template/im_bill_return_view/" + createdBillData.bill_id);
    } else {
      setErrors({
        ...errors,
        _billDataError: "Unable to submit bill data",
      });
      setLoading(false);
    }
  };

  const columns = [
    { id: "item", label: "Item", minWidth: 150 },
    { id: "brand", label: "Brand", minWidth: 120 },
    {
      id: "qty",
      label: "Return Qty",
      minWidth: 80,
      type: "number",
      isDecimal: false,
    },
    { id: "billUnit", label: "Bill Unit", minWidth: 120 },
  ];

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : `Rs: ${parseFloat(value || 0).toFixed(2)}`;
  };

  const getTypeChip = (billType) => {
    let color;
    let bgColor;

    switch (billType) {
      case "RETURN BILL":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        break;
      case "INTAKE BILL":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
    }

    return (
      <Chip
        label={billType}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading bill details...</Typography>
      </Box>
    );
  }

  if (errors._billDataError) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Unable to Submit Import Bill
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Please retry in a few minutes or contact support.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setOpenConfirmation(false)}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ReceiptIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Import Return Bill Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Bill Header Information */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TagIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Bill Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Number
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {importBill?.billNo}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {importBill?.addedBy}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Shop
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <StoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body1">{importBill?.shop}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Type
                  </Typography>
                  {getTypeChip(importBill?.billType)}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bill Note */}
      {importBill?.note && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <DescriptionIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Note
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ pl: 3, mt: 1 }}>
              {importBill?.note}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Bill Items Table */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <ShoppingBasketIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Bill Items
            </Typography>
            <Badge badgeContent={rows.length} color="primary" sx={{ ml: 1 }} />
          </Box>

          <Box
            sx={{
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <TableItem
              columns={columns}
              rows={rows}
              page={0}
              setPage={() => {}}
              tablePagin={false}
              totalPages={1}
              getData={fetchData}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setOpenConfirmation(false)}
        >
          Back To Edit
        </Button>
        <Button variant="contained" onClick={() => submitBill()}>
          Submit
        </Button>
      </Box>
    </Paper>
  );
}

export default Im_returnBillConfirmation;
